
import { Component, ElementRef, HostListener, OnInit, ViewChild, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UtilityService } from '../../../../services';
import { CommonFieldWidgetBase } from '../common-field-widget-base';
import { CommonMultiSelectField } from '../field';
import { getLabels } from '../field.labels';


@Component({
	selector: 'common-multiselect-dropdown-field',
	imports: [FormsModule],
	templateUrl: './common-multiselect-dropdown-field.widget.html',
	host: { '[class.d-none]': 'isExcluded()' },
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './common-multiselect-dropdown-field.widget.scss',
})
export class CommonMultiselectDropdownFieldWidget<T> extends CommonFieldWidgetBase<T[], CommonMultiSelectField<T>> implements OnInit {
	private util = inject(UtilityService);

	public readonly field = input.required<CommonMultiSelectField<T>>();

	public readonly labels = computed(() => getLabels(this.field().config().isSpanish));

	@ViewChild('multiSelectDropdown') multiSelectDropdown: ElementRef<HTMLElement> | undefined;
	@ViewChild('panel') panel: ElementRef<HTMLElement> | undefined;
	public open = signal(false);
	public placeAbove = signal(false);

	ngOnInit() {
		this.onInit(this.field());
	}

	@HostListener('document:click', ['$event'])
	onDocClick(e: MouseEvent) {
		if (!this.open()) return;
		if (e.target instanceof Node && !this.multiSelectDropdown?.nativeElement.contains(e.target)) this.close();
	}

	@HostListener('window:resize') onResize() {
		if (this.open()) this.recalcPlacement();
	}

	private recalcPlacement() {
		if (!this.multiSelectDropdown?.nativeElement || !this.panel?.nativeElement) return;

		// Get the bounding rectangle of the element
		const rect = this.multiSelectDropdown.nativeElement.getBoundingClientRect();
		const roomBelow = window.innerHeight - rect.bottom;
		const est = this.panel.nativeElement.offsetHeight; // panel height

		// Place dropdown above if:
		// 1. Not enough room below (roomBelow < est)
		// 2. Enough room above (rect.top > est)
		this.placeAbove.set(roomBelow < est && rect.top > est);
	}

	toggle() {
		if (this.open()) {
			this.close();
		} else {
			this.openPanel();
		}
	}

	openPanel() {
		this.recalcPlacement();
		this.open.set(true);
	}

	close() {
		this.open.set(false);
	}

	public onChange(value: T, checked: boolean) {
		const values = this.field().pendingValue() ?? this.field().actualValue();

		const filteredModel = values.filter(data => this.field().config().options.map(opt => opt.value).includes(data));

		const model: T[] = checked
			? [...filteredModel, value]
			: filteredModel.filter(data => data !== value);

		this.processChange(model);
	}

	public selectedCountLabel = computed(() => {
		const labels = this.labels();
		const values = this.field().pendingValue() ?? this.field().actualValue();
		return values.length === 0 ? labels.select() : `${values.length} ${values.length == 1 ? labels.selected() : labels.selecteds()}`;
	});

	public async onBlur() {
		await this.processBlur();
	}

	protected renderValue(value: T) {
		const values = this.field().pendingValue() ?? this.field().actualValue();

		return values.includes(value);
	}

	protected validate(values: T[]): string {

		const config = this.field().config();
		const labels = this.labels();

		if (!values.length && config.isRequired) return labels.missingRequired();
		if (values.length && values.find(v => !config.options.map(opt => opt.value).includes(v))) return labels.invalidSelection();

		return '';
	}

	public setFocus() {
		console.error('setFocus() not implemented');
	}
}